/**
 * feat-305 — the assistant panel's own on-screen copy (design NOTES §3–§6).
 * Page-supplied copy (title, subtitle, starters, intro, footer note) lives in
 * `pages.ts`. Per the repo rule, on-screen strings render through constants,
 * so a grep of a visible string lands here.
 */
import type { AssistantErrorCode } from './types';

export type ErrorTone = 'danger' | 'neutral';
export type ErrorAction = 'retry' | 'new' | 'none';
export type ErrorIconName =
  | 'circle-alert'
  | 'timer-off'
  | 'clock'
  | 'list-filter'
  | 'messages-square'
  | 'lock'
  | 'circle-off'
  | 'cloud-off';

export interface ErrorPresentation {
  tone: ErrorTone;
  icon: ErrorIconName;
  title: string;
  body: string;
  action: ErrorAction;
}

const UPSTREAM: ErrorPresentation = {
  tone: 'danger',
  icon: 'circle-alert',
  title: 'Something went wrong',
  body: "The answer didn't come through. Your question is still here, so you can try it again.",
  action: 'retry',
};

/**
 * Tone is by who can fix it (NOTES §4): a fault is danger, a limit or rule is
 * neutral. `refused`/`incomplete`/`internal`/`network`/`invalid`/`unauthorized`
 * have no bespoke design copy and take the generic fault box.
 */
export function errorPresentation(code: AssistantErrorCode): ErrorPresentation {
  switch (code) {
    case 'timeout':
      return {
        tone: 'danger',
        icon: 'timer-off',
        title: 'That took too long',
        body: 'Try again, or ask about a shorter stretch of time.',
        action: 'retry',
      };
    case 'limit_rate':
    case 'limit_busy':
      return {
        tone: 'neutral',
        icon: 'clock',
        title: "You've asked a lot in a short time",
        body: 'Give it a minute, then try again.',
        action: 'retry',
      };
    case 'limit_tool_calls':
      return {
        tone: 'neutral',
        icon: 'list-filter',
        title: 'That question needed too many lookups',
        body: 'Try a narrower question, like one month or one metric.',
        action: 'retry',
      };
    case 'limit_conversation':
      return {
        tone: 'neutral',
        icon: 'messages-square',
        title: 'This conversation is full',
        body: 'Start a new conversation to keep asking.',
        action: 'new',
      };
    case 'privacy_blocked':
      return {
        tone: 'neutral',
        icon: 'lock',
        title: 'This answer was stopped',
        body:
          'It was about to include what was said on a shift, which is private to the server. Try asking about scores or coaching themes instead.',
        action: 'none',
      };
    case 'not_found':
      // A 404 from chat means the tenant was disabled (or the context went away)
      // after the panel opened: the "disabled" class, composer disabled.
      return {
        tone: 'neutral',
        icon: 'circle-off',
        title: "The assistant isn't available right now",
        body: 'It has been turned off for this account.',
        action: 'none',
      };
    default:
      return UPSTREAM;
  }
}

export const RESTORE_ERROR: ErrorPresentation = {
  tone: 'neutral',
  icon: 'cloud-off',
  title: "Couldn't load your earlier conversation",
  body: 'You can try again, or start a new one.',
  action: 'retry',
};

export const ASSISTANT_COPY = {
  shiftsCited: 'Shifts cited',
  stopped: 'Stopped',
  thinking: 'Thinking…',
  tryAgain: 'Try again',
  newConversation: 'New conversation',
  newShort: 'New',
  close: 'Close assistant',
  closeTitle: 'Close (Esc)',
  send: 'Send',
  stop: 'Stop answer',
  stopTitle: 'Stop',
  jumpToLatest: 'Jump to latest',
  youAsked: 'You asked: ',
  assistantPrefix: 'Assistant: ',
  tryAsking: 'Try asking',
  conversationLabel: 'Conversation',
  loadingConversation: 'Loading your conversation…',
  didNotFinish: "This answer didn't finish.",
  answerReady: 'Answer ready.',
  answerStopped: 'Answer stopped.',
  newConversationStarted: 'New conversation started.',
  placeholderFallback: 'Ask a question…',
  ask: 'Ask',
  restoredDivider: (dateLabel: string) => `Your conversation from ${dateLabel} · only you can see it`,
  panelError: 'The assistant panel ran into a problem.',
  copyConversation: 'Copy conversation',
  copied: 'Copied',
  copyFailed: "Couldn't copy",
  /** Plain-text transcript labels (copy conversation). */
  plainYou: 'You',
  plainAssistant: 'Assistant',
  plainStopped: '(stopped)',
  plainDidNotFinish: "(didn't finish)",
} as const;
